/**
 * Dropdown.tsx
 *
 * Replicates molekule Dropdown API:
 *   Props: placement, width, trigger (required ReactElement), autoclose,
 *          offset, zIndex, transitionDuration, transitionTimingFunction,
 *          children or render function
 *   Static sub-components: Dropdown.Item, Dropdown.Divider, Dropdown.Title
 *
 * Used in:
 *   EllipsisDropdown.js — placement, width, trigger, Dropdown.Item (with color, id, onClick)
 *   Header.js           — placement, width, trigger, Dropdown.Item, Dropdown.Divider
 *
 * Built on @radix-ui/react-dropdown-menu. Styled with styled-components v5 (v6-compatible).
 */

import React, { createContext, useContext, useState } from 'react';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import styled, { css, keyframes } from 'styled-components';

// ---------------------------------------------------------------------------
// Placement mapping from molekule / Popper.js names → Radix side/align
// ---------------------------------------------------------------------------

type MolekulePlacement =
  | 'top'
  | 'top-start'
  | 'top-end'
  | 'bottom'
  | 'bottom-start'
  | 'bottom-end'
  | 'left'
  | 'left-start'
  | 'left-end'
  | 'right'
  | 'right-start'
  | 'right-end';

type RadixSide = 'top' | 'bottom' | 'left' | 'right';
type RadixAlign = 'start' | 'center' | 'end';

function toRadixSideAlign(placement: MolekulePlacement = 'bottom-start'): {
  side: RadixSide;
  align: RadixAlign;
} {
  const parts = placement.split('-');
  const side = parts[0] as RadixSide;
  const suffix = parts[1];
  const align: RadixAlign =
    suffix === 'start' ? 'start' : suffix === 'end' ? 'end' : 'center';
  return { side, align };
}

// Transform origins matching molekule's PLACEMENT_TRANSITION_ORIGINS
const TRANSFORM_ORIGINS: Record<string, string> = {
  'top-start': '0 100%',
  top: '50% 100%',
  'top-end': '100% 100%',
  'right-start': '0 0',
  right: '0 50%',
  'right-end': '0 100%',
  'bottom-start': '0 0',
  bottom: '50% 0',
  'bottom-end': '100% 0',
  'left-start': '100% 0',
  left: '100% 50%',
  'left-end': '100% 100%',
};

// ---------------------------------------------------------------------------
// Animations
// ---------------------------------------------------------------------------

const scaleIn = keyframes`
  from {
    opacity: 0.75;
    transform: scale(0.75);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

// ---------------------------------------------------------------------------
// Context — lets Dropdown.Item call close()
// ---------------------------------------------------------------------------

interface DropdownCtx {
  close: () => void;
}
const DropdownContext = createContext<DropdownCtx>({ close: () => {} });

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

interface MenuProps {
  $width?: number | string;
  $zIndex?: number;
  $placement?: string;
  $transitionDuration?: number;
  $transitionTimingFunction?: string;
}

const StyledContent = styled(DropdownMenuPrimitive.Content)<MenuProps>`
  z-index: ${(p) => p.$zIndex ?? 10};
  background: white;
  border-radius: 8px;
  border: 1px solid #e8e9ea;
  outline: none;
  box-shadow: 0px 2px 16px rgba(27, 32, 43, 0.1);
  width: ${(p) =>
    p.$width == null
      ? 'auto'
      : typeof p.$width === 'string'
        ? p.$width
        : `${p.$width}px`};
  padding: 8px;
  transform-origin: ${(p) => TRANSFORM_ORIGINS[p.$placement ?? 'bottom-start'] ?? '0 0'};

  &[data-state='open'] {
    animation: ${scaleIn} ${(p) => p.$transitionDuration ?? 225}ms
      ${(p) => p.$transitionTimingFunction ?? 'cubic-bezier(0.25, 0.1, 0.17, 1.2)'}
      forwards;
  }
`;

interface ItemProps {
  $color?: string;
  $disabled?: boolean;
  $selected?: boolean;
}

const StyledItem = styled(DropdownMenuPrimitive.Item)<ItemProps>`
  display: block;
  flex: 1;
  opacity: ${(p) => (p.$disabled ? 0.3 : 1)};
  pointer-events: ${(p) => (p.$disabled ? 'none' : 'initial')};
  user-select: ${(p) => (p.$disabled ? 'none' : 'initial')};
  text-decoration: none;
  color: ${(p) => p.$color || '#494D55'};
  cursor: pointer;
  margin: 0;
  padding: 8px;
  transition: 125ms background;
  outline: none;
  appearance: none;
  border: 0;
  border-radius: 8px;
  font: inherit;
  font-size: 14px;
  font-weight: 500;
  text-align: left;
  position: relative;
  background: transparent;
  width: 100%;
  box-sizing: border-box;

  &:hover,
  &:focus {
    color: ${(p) => p.$color || '#494D55'};
    background: #f4f4f4;
  }

  ${(p) =>
    p.$selected &&
    css`
      color: #0958f3;
      &:hover,
      &:focus {
        color: #0958f3;
      }
    `}
`;

const StyledSeparator = styled(DropdownMenuPrimitive.Separator)`
  display: block;
  height: 1px;
  background: #e8e9ea;
  border: 0;
  margin-left: -8px;
  margin-right: -8px;
  margin-top: 4px;
  margin-bottom: 4px;
`;

const StyledTitle = styled.span`
  display: block;
  font-weight: 700;
  color: #a4a6aa;
  padding: 4px 8px;
  font-size: 12px;
`;

// Trigger wrapper — align-self: flex-start like molekule
const TriggerWrapper = styled.div`
  align-self: flex-start;
  display: inline-block;
  outline: none;
`;

// ---------------------------------------------------------------------------
// Dropdown.Item
// ---------------------------------------------------------------------------

interface DropdownItemProps {
  children?: React.ReactNode;
  onClick?: () => void;
  closeOnClick?: boolean;
  color?: string;
  disabled?: boolean;
  selected?: boolean;
  icon?: string;
  iconProps?: Record<string, any>;
  id?: string;
  style?: React.CSSProperties;
  className?: string;
  [key: string]: any;
}

function DropdownItem({
  children,
  onClick,
  closeOnClick = true,
  color,
  disabled,
  selected,
  // absorb unsupported props
  icon: _icon,
  iconProps: _iconProps,
  id,
  style,
  className,
  ...rest
}: DropdownItemProps) {
  const { close } = useContext(DropdownContext);

  const handleSelect = () => {
    if (closeOnClick) close();
    if (typeof onClick === 'function') onClick();
  };

  return (
    <StyledItem
      id={id}
      style={style}
      className={className}
      $color={color}
      $disabled={disabled}
      $selected={selected}
      disabled={disabled}
      onSelect={handleSelect}
      {...rest}
    >
      {children}
    </StyledItem>
  );
}

// ---------------------------------------------------------------------------
// Dropdown.Divider
// ---------------------------------------------------------------------------

function DropdownDivider(props: {
  style?: React.CSSProperties;
  className?: string;
}) {
  return <StyledSeparator {...props} />;
}

// ---------------------------------------------------------------------------
// Dropdown.Title
// ---------------------------------------------------------------------------

function DropdownTitle({
  children,
  style,
  className,
}: {
  children?: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
}) {
  return (
    <StyledTitle style={style} className={className}>
      {children}
    </StyledTitle>
  );
}

// ---------------------------------------------------------------------------
// Main Dropdown
// ---------------------------------------------------------------------------

interface DropdownProps {
  trigger: React.ReactElement;
  placement?: MolekulePlacement;
  width?: number | string;
  autoclose?: boolean;
  offset?: string;
  zIndex?: number;
  transitionDuration?: number;
  transitionTimingFunction?: string;
  children?:
    React.ReactNode | ((opts: { close: () => void }) => React.ReactNode);
  render?: (opts: { close: () => void }) => React.ReactNode;
  // absorb unused molekule props
  positionFixed?: boolean;
  boundariesElement?: string;
  portalNode?: Element;
  styles?: Record<string, any>;
  style?: React.CSSProperties;
  className?: string;
  [key: string]: any;
}

function Dropdown({
  trigger,
  placement = 'bottom-start',
  width,
  autoclose = true,
  offset = '0, 10',
  zIndex = 10,
  transitionDuration = 225,
  transitionTimingFunction = 'cubic-bezier(0.25, 0.1, 0.17, 1.2)',
  children,
  render,
  // absorb props we don't need
  positionFixed: _pf,
  boundariesElement: _be,
  portalNode: _pn,
  styles: _styles,
  style: _style,
  className: _className,
  ...menuProps
}: DropdownProps) {
  const [open, setOpen] = useState(false);

  const close = () => setOpen(false);

  // Parse Radix side/align from molekule placement string
  const { side, align } = toRadixSideAlign(placement);

  // Parse offset string "x, y" to Radix sideOffset
  const sideOffset = (() => {
    if (!offset) return 10;
    const parts = offset.split(',');
    const y = parseFloat(parts[1] ?? parts[0]);
    return isNaN(y) ? 10 : y;
  })();

  const renderer = render || children;
  const renderedContent =
    typeof renderer === 'function' ? renderer({ close }) : renderer;

  return (
    <DropdownContext.Provider value={{ close }}>
      <DropdownMenuPrimitive.Root
        open={open}
        onOpenChange={setOpen}
        modal={false}
      >
        <DropdownMenuPrimitive.Trigger asChild>
          <TriggerWrapper>
            {React.cloneElement(trigger, {
              role: (trigger.props as any).role || 'button',
              tabIndex: (trigger.props as any).tabIndex ?? 0,
              'aria-haspopup': true,
              'aria-expanded': open,
              style: { cursor: 'pointer', ...(trigger.props as any).style },
            })}
          </TriggerWrapper>
        </DropdownMenuPrimitive.Trigger>

        <DropdownMenuPrimitive.Portal>
          <StyledContent
            side={side}
            align={align}
            sideOffset={sideOffset}
            $width={width}
            $zIndex={zIndex}
            $placement={placement}
            $transitionDuration={transitionDuration}
            $transitionTimingFunction={transitionTimingFunction}
            onInteractOutside={(e) => {
              // autoclose=false must actively suppress Radix's default close
              if (!autoclose) {
                e.preventDefault();
              } else {
                close();
              }
            }}
            data-testid="dropdown-menu"
            {...(menuProps as any)}
          >
            {renderedContent}
          </StyledContent>
        </DropdownMenuPrimitive.Portal>
      </DropdownMenuPrimitive.Root>
    </DropdownContext.Provider>
  );
}

Dropdown.Item = DropdownItem;
Dropdown.Divider = DropdownDivider;
Dropdown.Title = DropdownTitle;

export default Dropdown;
